import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useI18n, LocaleCode } from '../i18n';
import { Globe } from 'lucide-react';

const LANGUAGE_OPTIONS: { code: LocaleCode; name: string; flag: string }[] = [
	{ code: 'en', name: 'English', flag: '🇺🇸' },
	{ code: 'es', name: 'Español', flag: '🇪🇸' },
	{ code: 'fr-FR', name: 'Français', flag: '🇫🇷' },
	{ code: 'fr-CA', name: 'Français (CA)', flag: '🇨🇦' },
	{ code: 'de', name: 'Deutsch', flag: '🇩🇪' },
	{ code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
	{ code: 'pt', name: 'Português', flag: '🇵🇹' },
	{ code: 'it', name: 'Italiano', flag: '🇮🇹' },
];

export const LanguageSelector: React.FC = () => {
	const { locale, setLocale } = useI18n();
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
	const buttonRef = useRef<HTMLButtonElement>(null);

	const currentLanguage = LANGUAGE_OPTIONS.find(lang => lang.code === locale) || LANGUAGE_OPTIONS[0];

	console.log('LanguageSelector render - current locale:', locale, 'currentLanguage:', currentLanguage);

	// Update dropdown position when opening
	useEffect(() => {
		if (isOpen && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + window.scrollY + 4,
				left: rect.right - 160, // 160px is min-w-[160px]
				width: Math.max(160, rect.width)
			});
		}
	}, [isOpen]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			console.log('Click outside detected, isOpen:', isOpen);
			if (isOpen) {
				// Check if click is on the dropdown itself
				const target = event.target as Element;
				if (target && target.closest('[data-language-dropdown]')) {
					console.log('Click was on dropdown, not closing');
					return;
				}
				console.log('Click was outside dropdown, closing');
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [isOpen]);

	const dropdownContent = isOpen && (
		<div 
			data-language-dropdown
			className="fixed bg-white border-2 border-gray-300 rounded-lg shadow-xl z-[9999] min-w-[160px]"
			style={{
				top: `${dropdownPosition.top}px`,
				left: `${dropdownPosition.left}px`,
				width: `${dropdownPosition.width}px`,
				backgroundColor: 'white',
				boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
			}}
		>
			{LANGUAGE_OPTIONS.map((language) => (
				<button
					key={language.code}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						console.log('Language selected:', language.code);
						console.log('setLocale function:', setLocale);
						console.log('Current locale before change:', locale);
						setLocale(language.code);
						console.log('setLocale called with:', language.code);
						setIsOpen(false);
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.backgroundColor = '#f3f4f6';
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.backgroundColor = locale === language.code ? '#eff6ff' : 'white';
					}}
					className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors first:rounded-t-lg last:rounded-b-lg cursor-pointer ${
						locale === language.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
					}`}
					style={{ backgroundColor: locale === language.code ? '#eff6ff' : 'white' }}
				>
					<span className="text-lg">{language.flag}</span>
					<span>{language.name}</span>
					{locale === language.code && (
						<span className="ml-auto text-blue-600 font-bold">✓</span>
					)}
				</button>
			))}
		</div>
	);

	return (
		<>
			<div className="relative">
				<button
					ref={buttonRef}
					onClick={() => {
						console.log('Language selector clicked, isOpen:', isOpen);
						setIsOpen(!isOpen);
					}}
					className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors rounded-lg hover:bg-gray-50 border border-gray-200 bg-white"
					aria-label="Select language"
				>
					<Globe className="w-4 h-4" />
					<span className="text-lg">{currentLanguage.flag}</span>
					<span className="hidden sm:inline">{currentLanguage.name}</span>
				</button>
			</div>

			{/* Render dropdown as portal to escape overflow constraints */}
			{isOpen && createPortal(dropdownContent, document.body)}
		</>
	);
};
